import { useState, useRef, useEffect } from 'react';
import { Send, Smile, Paperclip, Image, FileText, MapPin, X, Reply, Loader2, Mic, Camera, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import VoiceRecorder from './VoiceRecorder';
import LocationPicker from './LocationPicker';
import ContactPicker from './ContactPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMOJI_CATEGORIES = [
  {
    key: 'spiritual',
    icon: '\u{1F549}\u{FE0F}',
    label: 'Spiritual',
    emojis: [
      '\u{1F64F}', '\u{1F549}\u{FE0F}', '\u{2728}', '\u{1F6A9}', '\u{1F338}', '\u{1F3F5}\u{FE0F}', '\u{1F54C}', '\u{1F3EF}',
      '\u{1F4FF}', '\u{1FA94}', '\u{1F56F}\u{FE0F}', '\u{2604}\u{FE0F}', '\u{1F31F}', '\u{2B50}', '\u{1F31E}', '\u{1F319}',
      '\u{1F30D}', '\u{1F305}', '\u{1F304}', '\u{1F307}', '\u{26EA}', '\u{1F54D}', '\u{2668}\u{FE0F}', '\u{1FA77}',
    ],
  },
  {
    key: 'smileys',
    icon: '\u{1F600}',
    label: 'Smileys',
    emojis: [
      '\u{1F600}', '\u{1F603}', '\u{1F604}', '\u{1F601}', '\u{1F606}', '\u{1F605}', '\u{1F602}', '\u{1F923}',
      '\u{1F60A}', '\u{1F607}', '\u{1F642}', '\u{1F643}', '\u{1F609}', '\u{1F60D}', '\u{1F970}', '\u{1F618}',
      '\u{1F617}', '\u{1F619}', '\u{1F61A}', '\u{1F60B}', '\u{1F61B}', '\u{1F61C}', '\u{1F92A}', '\u{1F61D}',
      '\u{1F911}', '\u{1F917}', '\u{1F92D}', '\u{1F92B}', '\u{1F914}', '\u{1F910}', '\u{1F928}', '\u{1F610}',
      '\u{1F611}', '\u{1F636}', '\u{1F60F}', '\u{1F612}', '\u{1F644}', '\u{1F62C}', '\u{1F925}', '\u{1F60C}',
      '\u{1F614}', '\u{1F62A}', '\u{1F924}', '\u{1F634}', '\u{1F637}', '\u{1F912}', '\u{1F915}', '\u{1F922}',
    ],
  },
  {
    key: 'hearts',
    icon: '\u{2764}\u{FE0F}',
    label: 'Love',
    emojis: [
      '\u{2764}\u{FE0F}', '\u{1F9E1}', '\u{1F49B}', '\u{1F49A}', '\u{1F499}', '\u{1F49C}', '\u{1F5A4}', '\u{1F90D}',
      '\u{1F90E}', '\u{1F498}', '\u{1F49D}', '\u{1F496}', '\u{1F497}', '\u{1F493}', '\u{1F49E}', '\u{1F495}',
      '\u{1F48C}', '\u{1F48B}', '\u{1F48D}', '\u{1F490}', '\u{1F339}', '\u{1F33A}', '\u{1F337}', '\u{1F33B}',
    ],
  },
  {
    key: 'gestures',
    icon: '\u{1F44D}',
    label: 'Gestures',
    emojis: [
      '\u{1F44D}', '\u{1F44E}', '\u{1F44A}', '\u{270A}', '\u{1F91B}', '\u{1F91C}', '\u{1F44F}', '\u{1F64C}',
      '\u{1F450}', '\u{1F91D}', '\u{1F64F}', '\u{270D}\u{FE0F}', '\u{1F485}', '\u{1F933}', '\u{1F4AA}', '\u{1F9B5}',
      '\u{1F9B6}', '\u{1F442}', '\u{1F443}', '\u{1F440}', '\u{1F441}\u{FE0F}', '\u{1F445}', '\u{1F444}', '\u{1F9E0}',
    ],
  },
  {
    key: 'nature',
    icon: '\u{1F33F}',
    label: 'Nature',
    emojis: [
      '\u{1F33F}', '\u{1F340}', '\u{1F341}', '\u{1F342}', '\u{1F343}', '\u{1F332}', '\u{1F333}', '\u{1F334}',
      '\u{1F335}', '\u{1F33E}', '\u{1F33C}', '\u{1F33B}', '\u{1F33A}', '\u{1F339}', '\u{1F337}', '\u{1F338}',
      '\u{1F30A}', '\u{1F30B}', '\u{1F3D4}\u{FE0F}', '\u{26C5}', '\u{1F308}', '\u{1F525}', '\u{1F4A7}', '\u{2744}\u{FE0F}',
    ],
  },
  {
    key: 'objects',
    icon: '\u{1F389}',
    label: 'Objects',
    emojis: [
      '\u{1F389}', '\u{1F38A}', '\u{1F388}', '\u{1F381}', '\u{1F380}', '\u{1F3C6}', '\u{1F396}\u{FE0F}', '\u{1F3C5}',
      '\u{1F4DA}', '\u{1F4D6}', '\u{1F4D6}', '\u{270F}\u{FE0F}', '\u{1F4DD}', '\u{1F4E7}', '\u{1F4F1}', '\u{1F4F7}',
      '\u{1F3B5}', '\u{1F3B6}', '\u{1F3A8}', '\u{1F3AD}', '\u{1F52E}', '\u{1F4A1}', '\u{1F4B0}', '\u{1F48E}',
    ],
  },
];

const QUICK_REPLIES = [
  { text: 'Har Har Mahadev \u{1F549}\u{FE0F}', icon: '\u{1F549}\u{FE0F}' },
  { text: 'Jai Shree Ram \u{1F6A9}', icon: '\u{1F6A9}' },
  { text: 'Om Namah Shivaya \u{1F64F}', icon: '\u{1F64F}' },
  { text: 'Radhe Radhe \u{2764}\u{FE0F}', icon: '\u{2764}\u{FE0F}' },
  { text: 'Jai Mata Di \u{1F338}', icon: '\u{1F338}' },
  { text: 'Om Shanti \u{2728}', icon: '\u{2728}' },
];

export default function MessageInput({ onSend, disabled, replyTo, onCancelReply, ws, conversationId }) {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [activeCategory, setActiveCategory] = useState('spiritual');
  const [recentEmojis, setRecentEmojis] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dharmbook_recent_emojis') || '[]'); } catch { return []; }
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);
  const attachRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (attachRef.current && !attachRef.current.contains(e.target)) setShowAttach(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '0px';
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = Math.min(scrollHeight, 120) + 'px';
    }
  }, [text]);

  useEffect(() => {
    setShowQuickReplies(text.length === 0 && !imageFile && !showEmoji);
  }, [text, imageFile, showEmoji]);

  const handleSend = async () => {
    if ((!text.trim() && !imageFile) || disabled || uploading) return;
    let mediaUrl = null;
    let mediaType = null;
    if (imageFile) {
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', imageFile);
        const token = localStorage.getItem('dharmbook_token');
        const uploadRes = await axios.post(`${API}/upload/image`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        mediaUrl = uploadRes.data.url || uploadRes.data.media_url;
        mediaType = 'image';
      } catch { toast.error('Failed to upload image'); setUploading(false); return; }
      setUploading(false);
    }
    onSend(text.trim(), mediaUrl, mediaType);
    setText('');
    clearImage();
    setShowEmoji(false);
    setShowAttach(false);
    if (textareaRef.current) textareaRef.current.style.height = '40px';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleTyping = () => {
    if (ws && ws.readyState === WebSocket.OPEN && conversationId) {
      ws.send(JSON.stringify({ type: 'typing', conversation_id: conversationId }));
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Only images allowed'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setShowAttach(false);
  };

  const clearImage = () => {
    setImageFile(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const insertEmoji = (emoji) => {
    setText(prev => prev + emoji);
    // Update recents
    const updated = [emoji, ...recentEmojis.filter(e => e !== emoji)].slice(0, 24);
    setRecentEmojis(updated);
    localStorage.setItem('dharmbook_recent_emojis', JSON.stringify(updated));
    textareaRef.current?.focus();
  };

  const handleQuickReply = (qr) => { onSend(qr.text, null, null); };
  const hasContent = text.trim() || imageFile;

  const handleVoiceSend = async (blob, recordedDuration) => {
    setIsRecording(false);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'voice.webm');
      const token = localStorage.getItem('dharmbook_token');
      const uploadRes = await axios.post(`${API}/upload/voice`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const mediaUrl = uploadRes.data.url || uploadRes.data.media_url;
      onSend(`Voice message (${Math.floor(recordedDuration / 60)}:${(recordedDuration % 60).toString().padStart(2, '0')})`, mediaUrl, 'voice');
    } catch (err) {
      console.error('Voice upload error:', err);
      toast.error('Failed to send voice message');
    }
    setUploading(false);
  };

  const handleVoiceCancel = () => {
    setIsRecording(false);
  };

  // Document upload handler
  const handleDocumentSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Document too large (max 25MB)');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.post(`${API}/upload/document`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const mediaUrl = res.data.url || res.data.media_url;
      const fileSize = res.data.file_size ? formatFileSize(res.data.file_size) : '';
      const text = JSON.stringify({ filename: file.name, size: fileSize, content_type: file.type });
      onSend(text, mediaUrl, 'document');
    } catch (err) {
      console.error('Document upload error:', err);
      toast.error('Failed to upload document');
    }
    setUploading(false);
    if (docInputRef.current) docInputRef.current.value = '';
  };

  // Location send handler
  const handleLocationSend = (location) => {
    setShowLocationPicker(false);
    const text = JSON.stringify(location);
    onSend(text, null, 'location');
  };

  // Contact send handler
  const handleContactSend = (contact) => {
    setShowContactPicker(false);
    const text = JSON.stringify(contact);
    onSend(text, null, 'contact');
  };

  const currentEmojis = activeCategory === 'recent'
    ? recentEmojis
    : EMOJI_CATEGORIES.find(c => c.key === activeCategory)?.emojis || [];

  if (disabled) {
    return (
      <div className="bg-slate-100 px-4 py-3 text-center flex-shrink-0 border-t border-slate-200">
        <p className="text-xs text-slate-500">You cannot send messages to this user</p>
      </div>
    );
  }

  if (isRecording) {
    return <VoiceRecorder onSend={handleVoiceSend} onCancel={handleVoiceCancel} />;
  }

  if (uploading) {
    return (
      <div className="flex items-center justify-center gap-2 px-4 py-3 bg-white border-t border-slate-200 flex-shrink-0">
        <Loader2 className="w-5 h-5 animate-spin text-[#FF9933]" />
        <span className="text-sm text-slate-500">Sending voice message...</span>
      </div>
    );
  }

  return (
    <div className="flex-shrink-0 bg-white border-t border-slate-200" data-testid="message-input">
      {/* Reply Preview */}
      {replyTo && (
        <div className="px-4 py-2 bg-orange-50 border-b border-orange-100 flex items-center justify-between" data-testid="reply-preview">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-1 h-8 bg-[#FF9933] rounded-full flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] font-semibold text-[#FF9933]">
                <Reply className="w-3 h-3 inline mr-1" />
                {replyTo.sender_name || 'Message'}
              </p>
              <p className="text-xs text-slate-500 truncate">{replyTo.text || '[Media]'}</p>
            </div>
          </div>
          <button onClick={onCancelReply} className="p-1 rounded-full hover:bg-orange-100 flex-shrink-0">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      )}

      {/* Quick Replies */}
      {showQuickReplies && !replyTo && (
        <div className="px-3 py-2 border-b border-slate-100 overflow-x-auto scrollbar-hide" data-testid="quick-replies">
          <div className="flex items-center gap-2">
            {QUICK_REPLIES.map((qr, i) => (
              <button
                key={i}
                onClick={() => handleQuickReply(qr)}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 hover:bg-orange-100 rounded-full text-xs text-[#E68A00] font-medium transition-colors whitespace-nowrap border border-orange-100"
                data-testid={`quick-reply-${i}`}
              >
                <span>{qr.icon}</span>
                <span>{qr.text.split(' ').slice(0, 3).join(' ')}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Image Preview */}
      {imagePreview && (
        <div className="px-4 py-2 border-b border-slate-200">
          <div className="relative inline-block">
            <img src={imagePreview} alt="" className="h-16 rounded-xl object-cover" />
            <button onClick={clearImage} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center shadow-sm">
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={handleImageSelect} />
      <input type="file" ref={docInputRef} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.zip" className="hidden" onChange={handleDocumentSelect} />

      {/* Input Row */}
      <div className="flex items-end gap-2 px-3 py-2" data-testid="input-row">
        <div className="flex-1 flex items-end bg-slate-100 rounded-full relative overflow-visible">
          {/* Emoji toggle */}
          <button
            onClick={() => { setShowEmoji(!showEmoji); setShowAttach(false); }}
            className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors flex-shrink-0 ${showEmoji ? 'text-[#FF9933]' : 'text-slate-400 hover:text-[#FF9933]'}`}
            data-testid="emoji-btn"
          >
            {showEmoji ? <X className="w-[20px] h-[20px]" /> : <Smile className="w-[22px] h-[22px]" />}
          </button>
          <div className="w-px h-5 bg-slate-300 flex-shrink-0 self-center" />
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => { setText(e.target.value); handleTyping(); }}
            onKeyDown={handleKeyDown}
            placeholder="Message"
            rows={1}
            className="flex-1 resize-none bg-transparent pl-3 pr-2 py-2.5 text-sm outline-none max-h-[120px] overflow-y-auto placeholder:text-slate-400"
            style={{ minHeight: '40px' }}
            data-testid="message-text-input"
          />
          <div className="flex items-center gap-0.5 pr-2 flex-shrink-0 self-center" ref={attachRef}>
            <button
              onClick={() => { setShowAttach(!showAttach); setShowEmoji(false); }}
              className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${showAttach ? 'text-[#FF9933]' : 'text-slate-400 hover:text-[#FF9933]'}`}
              data-testid="attach-btn"
            >
              <Paperclip className="w-[20px] h-[20px]" />
            </button>
            <button
              onClick={() => navigate('/camera', { state: { returnTo: '/messages', chat: { conversationId } } })}
              className="w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:text-[#FF9933] transition-colors"
              data-testid="camera-btn"
            >
              <Camera className="w-[20px] h-[20px]" />
            </button>
            {showAttach && (
              <div className="absolute bottom-full mb-2 right-2 bg-white rounded-2xl shadow-xl border border-slate-200 p-3 w-[200px] z-50" data-testid="attachment-popup">
                <div className="space-y-1">
                  <button onClick={() => { navigate('/camera', { state: { returnTo: '/messages', chat: { conversationId } } }); setShowAttach(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-orange-50 text-sm text-slate-700 transition-colors" data-testid="attach-camera">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center"><Camera className="w-4 h-4 text-red-600" /></div>Camera
                  </button>
                  <button onClick={() => { fileInputRef.current?.click(); setShowAttach(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-orange-50 text-sm text-slate-700 transition-colors" data-testid="attach-gallery">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center"><Image className="w-4 h-4 text-purple-600" /></div>Gallery
                  </button>
                  <button onClick={() => { docInputRef.current?.click(); setShowAttach(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-orange-50 text-sm text-slate-700 transition-colors" data-testid="attach-document">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center"><FileText className="w-4 h-4 text-blue-600" /></div>Document
                  </button>
                  <button onClick={() => { setShowLocationPicker(true); setShowAttach(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-orange-50 text-sm text-slate-700 transition-colors" data-testid="attach-location">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center"><MapPin className="w-4 h-4 text-green-600" /></div>Location
                  </button>
                  <button onClick={() => { setShowContactPicker(true); setShowAttach(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-orange-50 text-sm text-slate-700 transition-colors" data-testid="attach-contact">
                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center"><User className="w-4 h-4 text-orange-600" /></div>Contact
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Send / Mic */}
        {hasContent ? (
          <button onClick={handleSend} disabled={uploading} className="w-11 h-11 rounded-full bg-[#FF9933] text-white flex items-center justify-center hover:bg-[#E68A00] transition-all shadow-md disabled:opacity-50 flex-shrink-0" data-testid="send-btn">
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5 ml-0.5" />}
          </button>
        ) : (
          <button onClick={() => setIsRecording(true)} className="w-11 h-11 rounded-full bg-[#FF9933] text-white flex items-center justify-center hover:bg-[#E68A00] transition-all shadow-md flex-shrink-0" data-testid="mic-btn">
            <Mic className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Full-width Emoji Panel — WhatsApp style */}
      {showEmoji && (
        <div className="bg-[#1F2C33] border-t border-slate-700" data-testid="emoji-panel" style={{ height: '280px' }}>
          {/* Category tabs — top row */}
          <div className="flex items-center border-b border-slate-700 px-1">
            {/* Recents tab */}
            <button
              onClick={() => setActiveCategory('recent')}
              className={`flex-1 py-2.5 flex items-center justify-center transition-colors ${
                activeCategory === 'recent' ? 'border-b-2 border-[#FF9933]' : 'opacity-50 hover:opacity-80'
              }`}
              data-testid="emoji-tab-recent"
            >
              <span className="text-lg">&#128336;</span>
            </button>
            {EMOJI_CATEGORIES.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`flex-1 py-2.5 flex items-center justify-center transition-colors ${
                  activeCategory === cat.key ? 'border-b-2 border-[#FF9933]' : 'opacity-50 hover:opacity-80'
                }`}
                data-testid={`emoji-tab-${cat.key}`}
              >
                <span className="text-lg">{cat.icon}</span>
              </button>
            ))}
          </div>

          {/* Category label */}
          <div className="px-4 pt-2 pb-1">
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              {activeCategory === 'recent' ? 'Recently Used' : EMOJI_CATEGORIES.find(c => c.key === activeCategory)?.label}
            </p>
          </div>

          {/* Emoji Grid — large, scrollable */}
          <div className="overflow-y-auto px-2" style={{ height: '210px' }}>
            {currentEmojis.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-slate-500 text-sm">No recent emojis yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-8 gap-0.5" data-testid="emoji-grid">
                {currentEmojis.map((emoji, i) => (
                  <button
                    key={`${emoji}-${i}`}
                    onClick={() => insertEmoji(emoji)}
                    className="aspect-square rounded-lg hover:bg-white/10 flex items-center justify-center transition-transform active:scale-90"
                    data-testid={`emoji-${i}`}
                  >
                    <span className="text-[28px] leading-none">{emoji}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Location Picker Modal */}
      {showLocationPicker && (
        <LocationPicker
          onSend={handleLocationSend}
          onClose={() => setShowLocationPicker(false)}
        />
      )}

      {/* Contact Picker Modal */}
      {showContactPicker && (
        <ContactPicker
          onSend={handleContactSend}
          onClose={() => setShowContactPicker(false)}
        />
      )}
    </div>
  );
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
